// Paket P2 — Frontend-API für das Hinweis-System „Mitgliedschaft".
//
// Spricht den Server-Router `server/membership-promo.js` unter
// `/api/membership/promo` an. Die Funktionen sind dünne Wrapper um
// `apiRequest`; Typen sind hier zentral, damit Provider und Tests sie teilen.

import { apiRequest } from './client';

export type PromoSegment =
  | 'free_new'
  | 'free_lapsed'
  | 'plus'
  | 'plus_trial'
  | 'ultra'
  | 'none';

export type PromoDecision = 'modal' | 'banner' | 'none';

export type PromoSurface = 'adventure' | 'character' | 'group';
export type PromoQuotaKind = 'coins' | 'messages' | 'live';

export interface PromoRulesSummary {
  app_start_every: number;
  message_every: number;
  context_after: number;
  banner_cooldown_h: number;
}

export interface PromoState {
  app_starts: number;
  messages_total: number;
  last_modal_at: string | null;
  last_banner_at: string | null;
  last_context_at: string | null;
  snooze_until: string | null;
  dismiss_streak: number;
  intro_seen_at: string | null;
  quota_banner_day: string | null;
  quota_banner_week: string | null;
  context_after_count: number;
  // S8 Onboarding-Felder (additiv). Wahl, Schritt-Index der Vorteilswoche,
  // Startzeitpunkt und Opt-in fuer die Trial-Erinnerung. 'skip' ist ein
  // serverseitiger Sentinel, der die Vorteilswoche explizit beendet (P2-3).
  onboarding_choice: 'writing' | 'listening' | 'design' | 'skip' | null;
  onboarding_step: number;
  onboarding_started_at: string | null;
  payment_reminder_opt_in: boolean;
}

export interface OnboardingInfo {
  /** 0 = nichts gewaehlt, 1..3 = Schritt der Vorteilswoche. */
  step: number;
  choice: 'writing' | 'listening' | 'design' | null;
  /** Rotations-Vorteil, der als naechstes im Banner erscheinen soll. */
  next_benefit: 'writing' | 'listening' | 'design' | null;
  /** P2-3: wurde uebersprungen (Server-Sentinel 'skip'). */
  skipped?: boolean;
}

export interface TrialReminderInfo {
  due: boolean;
  ends_at: string | null;
}

export interface PromoSnapshot {
  enabled?: boolean;
  segment: PromoSegment;
  target_tier: string | null;
  intro_due: boolean;
  preview: PromoSegment | null;
  rules: PromoRulesSummary;
  state: PromoState;
  onboarding?: OnboardingInfo;
  trial_reminder?: TrialReminderInfo;
}

export interface PromoEventParams {
  character_name?: string;
  [key: string]: unknown;
}

export interface PromoEventResponse {
  decision: PromoDecision;
  variant: string | null;
  target_tier: string | null;
  surface: PromoSurface | null;
  params: PromoEventParams;
  preview?: boolean;
}

export function getPromoState(): Promise<PromoSnapshot> {
  return apiRequest<PromoSnapshot>('/api/membership/promo');
}

export interface PostPromoEventOptions {
  event: 'app_start' | 'messages' | 'context' | 'quota';
  surface?: PromoSurface;
  /** Seit dem letzten Event gesendete Nachrichten (Bündelung). */
  count?: number;
  character_name?: string;
  quota_kind?: PromoQuotaKind;
  /** Admin-Vorschau: nur sichtbar, wenn is_admin. */
  preview_segment?: PromoSegment;
  /** Stabile Client-ID für die Conversion-Messung. */
  exposure_id?: string;
}

export function postPromoEvent(opts: PostPromoEventOptions): Promise<PromoEventResponse> {
  return apiRequest<PromoEventResponse>('/api/membership/promo/event', {
    method: 'POST',
    body: {
      event: opts.event,
      surface: opts.surface,
      count: typeof opts.count === 'number' ? opts.count : undefined,
      character_name: opts.character_name,
      quota_kind: opts.quota_kind,
      preview_segment: opts.preview_segment,
      exposure_id: opts.exposure_id,
    },
  });
}

export type PromoAckAction = 'later' | 'open' | 'dismiss_banner' | 'purchased';

export interface AckPromoOptions {
  variant?: string;
  exposure_id?: string;
  tier?: string;
  duration?: string;
  source?: string;
}

export function ackPromo(id: string, action: PromoAckAction, opts?: AckPromoOptions | string): Promise<{ ok: boolean }> {
  // Rückwärtskompatibel: alte Aufrufer reichen die Variante als dritten String.
  const normalized: AckPromoOptions | undefined = typeof opts === 'string'
    ? (opts ? { variant: opts } : undefined)
    : opts;
  return apiRequest<{ ok: boolean }>('/api/membership/promo/ack', {
    method: 'POST',
    body: {
      id,
      action,
      variant: normalized?.variant,
      exposure_id: normalized?.exposure_id,
      tier: normalized?.tier,
      duration: normalized?.duration,
      source: normalized?.source,
    },
  });
}

export function markIntroSeen(): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>('/api/membership/promo/intro-seen', { method: 'POST' });
}

// ─── S8 Onboarding nach Abo-Start ────────────────────────────────────────
export type OnboardingChoice = 'writing' | 'listening' | 'design' | 'skip';

export interface PostOnboardingChoiceOptions {
  choice: OnboardingChoice;
  /** Optional: hochgezaehlter Schritt nach erfolgreichem Banner (1..3). */
  step?: number;
  /** Trial-Erinnerungs-Opt-in. Wird uebergeben, wenn der Nutzer im Dialog
   *  den Schalter betaetigt; das Server-INSERT laeuft idempotent. */
  payment_reminder_opt_in?: boolean;
}

export function postOnboardingChoice(opts: PostOnboardingChoiceOptions): Promise<{ ok: boolean; choice: string | null; step: number }> {
  return apiRequest<{ ok: boolean; choice: string | null; step: number }>(
    '/api/membership/promo/onboarding-choice',
    {
      method: 'POST',
      body: {
        choice: opts.choice,
        step: typeof opts.step === 'number' ? opts.step : undefined,
        payment_reminder_opt_in: typeof opts.payment_reminder_opt_in === 'boolean'
          ? opts.payment_reminder_opt_in : undefined,
      },
    },
  );
}

export type TrialReminderAckAction = 'shown' | 'dismiss' | 'manage';

export function postTrialReminderAck(action: TrialReminderAckAction): Promise<{ ok: boolean; recorded?: boolean }> {
  return apiRequest<{ ok: boolean; recorded?: boolean }>(
    '/api/membership/promo/trial-reminder-ack',
    { method: 'POST', body: { action } },
  );
}

// ─── S6 Funnel ────────────────────────────────────────────────────────────
export type FunnelEvent =
  | 'purchase_verified'
  | 'promo_view'
  | 'promo_dismiss'
  | 'promo_click'
  | 'paywall_view'
  | 'checkout_started'
  | 'checkout_cancelled'
  | 'checkout_failed'
  | 'first_benefit_used';

export interface FunnelEventPayload {
  event: FunnelEvent;
  exposure_id?: string;
  variant?: string;
  source?: string;
  tier?: string;
  duration?: string;
  platform?: string;
  discount?: string;
  meta?: Record<string, unknown>;
}

export function reportPromoView(input: { id: string; variant?: string; surface?: string }): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>('/api/membership/promo/view', {
    method: 'POST',
    body: input,
  });
}

export function reportFunnelEvent(payload: FunnelEventPayload): Promise<{ ok: boolean; recorded?: boolean }> {
  return apiRequest<{ ok: boolean; recorded?: boolean }>('/api/membership/funnel', {
    method: 'POST',
    body: {
      event: payload.event,
      exposure_id: payload.exposure_id,
      variant: payload.variant,
      source: payload.source,
      tier: payload.tier,
      duration: payload.duration,
      platform: payload.platform,
      discount: payload.discount,
      meta: payload.meta,
    },
  });
}
